import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Header } from "@/components/Header";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  MagnifyingGlass,
  FileCsv,
  FilePdf,
  FunnelSimple,
  X,
  MapPin,
  Receipt,
  ArrowRight,
  WhatsappLogo,
  BookmarkSimple,
  Trash,
  ShareNetwork,
  User,
} from "@phosphor-icons/react";

const INR = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
const MAX_Q_CHARS = 200;

export default function Pricelist() {
  const nav = useNavigate();
  const [services, setServices] = useState([]);
  const [columns, setColumns] = useState([]);
  const [meta, setMeta] = useState({ categories: [], cities: [] });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());

  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [city, setCity] = useState("all");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");

  // Saved presets
  const [presets, setPresets] = useState([]);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [presetShared, setPresetShared] = useState(false);
  const [me, setMe] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { q: q || undefined, category, city };
      if (minPrice) params.min_price = Number(minPrice);
      if (maxPrice) params.max_price = Number(maxPrice);
      const [svcRes, colRes, metaRes] = await Promise.all([
        api.get("/services", { params }),
        api.get("/columns"),
        api.get("/services/meta"),
      ]);
      setServices(svcRes.data);
      setColumns(colRes.data);
      setMeta(metaRes.data);
    } catch (e) {
      toast.error("Failed to load pricelist");
    } finally {
      setLoading(false);
    }
  };

  const loadPresets = async () => {
    try {
      const [ps, meRes] = await Promise.all([api.get("/saved-searches"), api.get("/auth/me")]);
      setPresets(ps.data);
      setMe(meRes.data);
    } catch {/* ignore */}
  };

  useEffect(() => { load(); loadPresets(); }, []);

  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
  }, [q, category, city, minPrice, maxPrice]);

  const resetFilters = () => {
    setQ(""); setCategory("all"); setCity("all"); setMinPrice(""); setMaxPrice("");
  };

  const activeFilters = useMemo(
    () => (q ? 1 : 0) + (category !== "all" ? 1 : 0) + (city !== "all" ? 1 : 0) + (minPrice ? 1 : 0) + (maxPrice ? 1 : 0),
    [q, category, city, minPrice, maxPrice]
  );

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allVisibleSelected = services.length > 0 && services.every((s) => selected.has(s.id));
  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        services.forEach((s) => next.delete(s.id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        services.forEach((s) => next.add(s.id));
        return next;
      });
    }
  };

  const goToQuote = () => {
    if (selected.size === 0) return;
    nav(`/quotes/new?ids=${Array.from(selected).join(",")}`);
  };

  const cellValue = (svc, col) => {
    if (col.key === "price_per_unit") return INR.format(svc.price_per_unit || 0);
    if (col.key === "cities") {
      const list = Array.isArray(svc.cities) ? svc.cities : (svc.city ? [svc.city] : []);
      if (list.length === 0) return "—";
      return (
        <div className="flex flex-wrap gap-1.5">
          {list.map((c, i) => (
            <span key={`${c}-${i}`} className="city-chip"><MapPin size={11} weight="fill" /> {c}</span>
          ))}
        </div>
      );
    }
    if (col.system) return svc[col.key] ?? "";
    return (svc.extra_fields || {})[col.key] ?? "";
  };

  const exportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (category !== "all") params.set("category", category);
      if (city !== "all") params.set("city", city);
      if (minPrice) params.set("min_price", minPrice);
      if (maxPrice) params.set("max_price", maxPrice);
      const token = localStorage.getItem("bw_token");
      const res = await fetch(`${API}/services/export.csv?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "brandonwheelz_pricelist.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const exportPDF = () => {
    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor("#C62828");
      doc.text("BrandOnWheelz", 40, 40);
      doc.setFontSize(10);
      doc.setTextColor("#111");
      doc.text("Services Pricelist", 40, 58);
      doc.setFontSize(9);
      doc.setTextColor("#555");
      doc.text(`Generated: ${new Date().toLocaleString()}`, 40, 72);

      const head = [columns.map((c) => c.label)];
      const body = services.map((s) =>
        columns.map((c) => {
          if (c.key === "price_per_unit") return INR.format(s.price_per_unit || 0);
          if (c.key === "cities") return (Array.isArray(s.cities) ? s.cities : []).join(", ");
          if (c.system) return s[c.key] ?? "";
          return (s.extra_fields || {})[c.key] ?? "";
        })
      );
      autoTable(doc, {
        head,
        body,
        startY: 90,
        styles: { fontSize: 8, cellPadding: 4, textColor: [20, 20, 26] },
        headStyles: { fillColor: [198, 40, 40], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [253, 250, 244] },
        margin: { left: 40, right: 40 },
      });
      doc.save("brandonwheelz_pricelist.pdf");
      toast.success("PDF downloaded");
    } catch {
      toast.error("PDF export failed");
    }
  };

  const copyWhatsApp = async () => {
    try {
      if (services.length === 0) {
        toast.error("No services to copy");
        return;
      }
      const top = services.slice(0, 25);
      const filterBits = [];
      if (q) filterBits.push(q);
      if (category !== "all") filterBits.push(category);
      if (city !== "all") filterBits.push(city);
      const header = filterBits.length ? `*BrandOnWheelz — ${filterBits.join(" · ")}*` : "*BrandOnWheelz Pricelist*";
      const lines = [header, ""];
      top.forEach((s, i) => {
        const cities = Array.isArray(s.cities) ? s.cities.join(", ") : "";
        const type = (s.extra_fields || {}).type;
        const bits = [
          `${i + 1}. *${s.service_name}*`,
          `   ₹${(s.price_per_unit || 0).toLocaleString("en-IN")}` + (s.duration ? ` / ${s.duration}` : "") + (s.moq ? ` · MOQ ${s.moq}` : ""),
        ];
        if (cities) bits.push(`   📍 ${cities}`);
        if (type) bits.push(`   ${type}`);
        lines.push(bits.join("\n"));
      });
      if (services.length > top.length) {
        lines.push("", `_+ ${services.length - top.length} more services. Ask for the full pricelist._`);
      }
      lines.push("", "_Rates valid 30 days. 50% advance to confirm booking._");
      const text = lines.join("\n");
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${top.length} services for WhatsApp`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const applyPreset = (p) => {
    setQ(p.q || "");
    setCategory(p.category || "all");
    setCity(p.city || "all");
    setMinPrice(p.min_price != null ? String(p.min_price) : "");
    setMaxPrice(p.max_price != null ? String(p.max_price) : "");
    setPresetsOpen(false);
    toast.success(`Preset "${p.name}" applied`);
  };

  const savePreset = async () => {
    const name = presetName.trim();
    if (!name) { toast.error("Give the preset a name"); return; }
    try {
      const payload = {
        name,
        q: q || "",
        category,
        city,
        min_price: minPrice ? Number(minPrice) : null,
        max_price: maxPrice ? Number(maxPrice) : null,
        is_shared: presetShared,
      };
      const { data } = await api.post("/saved-searches", payload);
      setPresets((prev) => [data, ...prev]);
      setPresetName("");
      setPresetShared(false);
      toast.success(`Saved "${name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const deletePreset = async (p) => {
    if (!window.confirm(`Delete preset "${p.name}"?`)) return;
    try {
      await api.delete(`/saved-searches/${p.id}`);
      setPresets((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const canDelete = (p) => me && (me.role === "admin" || p.created_by_id === me.id);

  return (
    <div className="min-h-screen bg-canvas pb-24" data-testid="pricelist-page">
      <Header />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 animate-fade-up">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-brand mb-3 font-bold">
              Services · Pricelist
            </div>
            <h1 className="font-display font-black text-zinc-900 text-4xl sm:text-5xl leading-[0.95] tracking-tight">
              Every rate. Every city. <span className="text-brand italic">One console.</span>
            </h1>
            <p className="text-zinc-500 mt-3 max-w-2xl">
              Search, filter, tick the services you need and turn them into a client quote in one click.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Button onClick={copyWhatsApp} variant="outline" data-testid="whatsapp-copy-btn"
              className="bg-white border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 text-emerald-700 gap-2">
              <WhatsappLogo size={18} weight="duotone" /> Copy WhatsApp
            </Button>
            <Button onClick={exportCSV} variant="outline" data-testid="export-csv-btn"
              className="bg-white border-zinc-300 hover:bg-brand-soft hover:border-brand hover:text-brand text-zinc-700 gap-2">
              <FileCsv size={18} weight="duotone" /> Export CSV
            </Button>
            <Button onClick={exportPDF} variant="outline" data-testid="export-pdf-btn"
              className="bg-white border-zinc-300 hover:bg-brand-soft hover:border-brand hover:text-brand text-zinc-700 gap-2">
              <FilePdf size={18} weight="duotone" /> Export PDF
            </Button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="rounded-lg border border-zinc-200 bg-white p-4 mb-6 shadow-sm" data-testid="filters-toolbar">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                placeholder='Try "Auto Delhi" or "Cab, Bengaluru, Mumbai" · commas = OR, spaces = AND'
                value={q}
                onChange={(e) => setQ(e.target.value.slice(0, MAX_Q_CHARS))}
                maxLength={MAX_Q_CHARS}
                data-testid="search-input"
                className="pl-10 h-11 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus-visible:ring-brand focus-visible:ring-offset-0"
              />
            </div>

            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="category-filter" className="w-full lg:w-52 h-11 bg-white border-zinc-300 text-zinc-800">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={city} onValueChange={setCity}>
              <SelectTrigger data-testid="city-filter" className="w-full lg:w-44 h-11 bg-white border-zinc-300 text-zinc-800">
                <SelectValue placeholder="City" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {meta.cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <Input type="number" placeholder="Min ₹" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} data-testid="min-price" className="w-24 h-11 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400" />
              <span className="text-zinc-400">—</span>
              <Input type="number" placeholder="Max ₹" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} data-testid="max-price" className="w-24 h-11 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400" />
            </div>

            <Popover open={presetsOpen} onOpenChange={setPresetsOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="presets-btn"
                  className="bg-white border-zinc-300 hover:bg-brand-soft hover:border-brand hover:text-brand text-zinc-700 gap-2 h-11">
                  <BookmarkSimple size={16} weight="duotone" />
                  Presets{presets.length > 0 && <span className="text-xs text-zinc-500 font-mono-num">({presets.length})</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-96 bg-white border-zinc-200 text-zinc-900 p-0" data-testid="presets-popover">
                <div className="p-4 border-b border-zinc-100">
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2">Save current view</div>
                  <div className="flex gap-2">
                    <Input
                      value={presetName}
                      onChange={(e) => setPresetName(e.target.value)}
                      placeholder="e.g. Auto Hood — South India"
                      data-testid="preset-name-input"
                      className="bg-white border-zinc-300 text-sm h-9"
                    />
                    <Button size="sm" onClick={savePreset} data-testid="save-preset-btn"
                      className="bg-brand hover:bg-brand-hover text-white h-9 px-3">
                      Save
                    </Button>
                  </div>
                  <label className="mt-2 flex items-center gap-2 text-xs text-zinc-600 cursor-pointer select-none">
                    <Checkbox checked={presetShared} onCheckedChange={(v) => setPresetShared(!!v)} data-testid="preset-shared" />
                    Share with team
                  </label>
                </div>
                <div className="max-h-72 overflow-auto">
                  {presets.length === 0 ? (
                    <div className="p-6 text-center text-sm text-zinc-400">
                      No saved presets yet. Set some filters + save this view.
                    </div>
                  ) : presets.map((p) => (
                    <div key={p.id} data-testid={`preset-${p.id}`} className="group flex items-start gap-2 px-4 py-2.5 hover:bg-brand-soft/40 border-b border-zinc-100 last:border-0">
                      <button type="button" onClick={() => applyPreset(p)} data-testid={`apply-preset-${p.id}`}
                        className="flex-1 text-left min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-semibold text-zinc-900 truncate">{p.name}</span>
                          {p.is_shared && <ShareNetwork size={12} weight="bold" className="text-brand" />}
                        </div>
                        <div className="text-[11px] text-zinc-500 mt-0.5 truncate">
                          {[p.q && `"${p.q}"`, p.category !== "all" && p.category, p.city !== "all" && p.city].filter(Boolean).join(" · ") || "no filters"}
                          {p.created_by_email && me && p.created_by_id !== me.id && (
                            <span className="ml-1 inline-flex items-center gap-1"><User size={10} weight="bold" /> {p.created_by_email}</span>
                          )}
                        </div>
                      </button>
                      {canDelete(p) && (
                        <button type="button" onClick={() => deletePreset(p)} data-testid={`del-preset-${p.id}`}
                          className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-red-600 p-1 transition-opacity">
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {activeFilters > 0 && (
              <Button variant="ghost" onClick={resetFilters} data-testid="reset-filters" className="text-zinc-600 hover:text-brand hover:bg-brand-soft gap-1">
                <X size={16} /> Clear ({activeFilters})
              </Button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden shadow-sm">
          <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200 bg-cream">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold flex items-center gap-2">
              <FunnelSimple size={14} />
              {loading ? "Loading..." : `${services.length} services`}
              {selected.size > 0 && (
                <span className="ml-2 text-brand" data-testid="selected-count">· {selected.size} selected</span>
              )}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono-num flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 bg-emerald-500 rounded-full animate-pulse-dot" />
              LIVE · updated in real time
            </div>
          </div>
          <div className="overflow-auto max-h-[calc(100vh-20rem)]">
            <table className="pricelist" data-testid="pricelist-table">
              <thead>
                <tr>
                  <th className="w-10">
                    <Checkbox checked={allVisibleSelected} onCheckedChange={toggleSelectAll} data-testid="select-all" aria-label="Select all" />
                  </th>
                  {columns.map((c) => <th key={c.key}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {!loading && services.length === 0 && (
                  <tr>
                    <td colSpan={columns.length + 1} className="text-center text-zinc-400 py-16">
                      No services match the current filters.
                    </td>
                  </tr>
                )}
                {services.map((svc, i) => (
                  <tr key={svc.id} data-testid={`service-row-${svc.id}`} className={`animate-fade-up ${selected.has(svc.id) ? "bg-brand-soft/40" : ""}`} style={{ animationDelay: `${Math.min(i, 12) * 25}ms` }}>
                    <td>
                      <Checkbox checked={selected.has(svc.id)} onCheckedChange={() => toggleSelect(svc.id)} data-testid={`select-${svc.id}`} aria-label={`Select ${svc.service_name}`} />
                    </td>
                    {columns.map((c) => (
                      <td key={c.key} className={c.key === "price_per_unit" ? "font-mono-num text-brand font-bold" : c.key === "service_name" ? "text-zinc-900 font-semibold" : ""}>
                        {cellValue(svc, c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Sticky selection bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-zinc-900 text-white rounded-full shadow-2xl border border-zinc-800 pl-5 pr-2 py-2 flex items-center gap-4 animate-fade-up" data-testid="selection-bar">
          <Receipt size={18} weight="duotone" className="text-brand" />
          <span className="text-sm">
            <span className="font-mono-num font-bold" data-testid="selection-bar-count">{selected.size}</span> service{selected.size === 1 ? "" : "s"} selected
          </span>
          <button onClick={() => setSelected(new Set())} className="text-xs text-zinc-400 hover:text-white transition-colors" data-testid="clear-selection">Clear</button>
          <Button onClick={goToQuote} data-testid="generate-quote-btn"
            className="bg-brand hover:bg-brand-hover text-white rounded-full h-10 px-5 gap-1.5 font-semibold">
            Generate Quote <ArrowRight size={16} weight="bold" />
          </Button>
        </div>
      )}
    </div>
  );
}
